import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Card,
  Typography,
  useMediaQuery,
  Stack,
  Chip,
  Avatar,
} from "@mui/material";
import {
  useDeleteConversationsMutation,
  useGetConversationsQuery,
  useGetTeamQuery,
  useGetUserInfoOnyQuery,
} from "../../../Api/attoDeskApi";
import { IToDo, ITeam, IUserAdmin } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import Pagination from "../../../Components/Pagination";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Autocomplete from "@mui/material/Autocomplete";
import useUser from "../../../Core/hook/useUser";
import GroupIcon from "@mui/icons-material/Group";
import { styled, useTheme } from "@mui/material/styles";
import { BASENAME } from "../../../config/index";
import DeleteIcon from "@mui/icons-material/Delete";
import Badge from "@mui/material/Badge";
import MailIcon from "@mui/icons-material/Mail";
import { Link } from "react-router-dom";

const capitalizeFirstLetter = (name: string | null | undefined) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { userHasRight } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IToDo | null>(null);

  const { data, isLoading, isError, refetch } = useGetConversationsQuery(
    { searchText: currentSearchQuery, staffId: "", id: "" },
    {
      pollingInterval: 2000,
    }
  );

  const { data: teamData, isLoading: teamsLoading } = useGetTeamQuery({
    searchText: "",
    id: "",
  });

  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const cliendList = useMemo(() => {
    return (teamData?.data ?? []) as ITeam[];
  }, [teamData?.data]);

  const userList = useMemo(() => {
    return userData?.data as IUserAdmin[];
  }, [userData?.data]);

  const staffMap = useMemo(() => {
    const map = new Map();
    cliendList?.forEach((category) => {
      map.set(
        category.id,
        capitalizeFirstLetter(category.teamFirstName || "") +
          " " +
          capitalizeFirstLetter(category.teamLastName || "")
      );
    });
    return map;
  }, [cliendList]);

  const userMap = useMemo(() => {
    const map = new Map();
    userList?.forEach((user) => {
      map.set(user.id, `${user.staff}`);
    });
    return map;
  }, [userList]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOpenDeletePopup = (coursing: IToDo) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteConversationsMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the position");
    }
  };

  if (isLoading || userIsLoading || teamsLoading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );

  const coursings: IToDo[] = Array.isArray(data?.data)
    ? (data?.data as IToDo[])
    : [];

  const handleAddProductClick = () => {
    navigate(`/conversations/create`);
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.card} sx={{ minHeight: 500 }}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ padding: 2, borderBottom: 4 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ display: "flex" }}
                >
                  <Grid item>
                    <TextField
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CiSearch className={classes.searchWidgetIcon} />
                          </InputAdornment>
                        ),
                        classes: {
                          root: classes.formControlList,
                        },
                      }}
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Subject .."
                      size="small"
                      className={classes.searchTextField}
                    />
                  </Grid>

                  <Grid item>
                    <Link
                      to={`/conversations/create`}
                      className={classes.iconLinkPropNormal}
                    >
                      <Button
                        // variant="contained"
                        className={classes.addNewButton}
                        onClick={handleAddProductClick}
                      >
                        Add New
                      </Button>
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                {isError ? (
                  <Grid container justifyContent="center" sx={{ padding: 2 }}>
                    <Typography>Error fetching data</Typography>
                  </Grid>
                ) : isLoading ? (
                  <Grid container justifyContent="center" sx={{ padding: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10vh"
                    >
                      <CircularProgress />
                    </Box>
                  </Grid>
                ) : coursings.length === 0 ? (
                  <Grid container justifyContent="center" sx={{ padding: 2 }}>
                    <Typography>No data available</Typography>
                  </Grid>
                ) : (
                  <Grid container spacing={3} sx={{ padding: 2 }}>
                    {coursings
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((group: IToDo) => (
                        <Grid item xs={12} key={group.id}>
                          <Card
                            elevation={3}
                            sx={{
                              borderRadius: 3,
                              p: 3,
                              bgcolor: theme.palette.cardAdd.bgColor,
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          >
                            <Grid container alignItems="center" spacing={2}>
                              <Grid
                                item
                                xs={12}
                                md={8}
                                container
                                alignItems="center"
                                spacing={2}
                                onClick={() =>
                                  navigate(
                                    `/conversations/messages/${group.id}`
                                  )
                                }
                                sx={{
                                  cursor: "pointer",
                                }}
                              >
                                <Grid item>
                                  <Badge
                                    badgeContent={
                                      group.unread_count > 0
                                        ? group.unread_count
                                        : null
                                    }
                                    color="primary"
                                    overlap="circular"
                                    anchorOrigin={{
                                      vertical: "top",
                                      horizontal: "right",
                                    }}
                                    sx={{
                                      "& .MuiBadge-badge": {
                                        fontWeight: "bold",
                                        fontSize: "0.75rem",
                                        minWidth: 20,
                                        height: 20,
                                        borderRadius: "50%",
                                        backgroundColor: "green",
                                        color: "white",
                                        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                                      },
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        bgcolor: "primary.main",
                                        width: 60,
                                        height: 60,
                                        fontSize: 28,
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {group.subject ? (
                                        group.subject.charAt(0).toUpperCase()
                                      ) : (
                                        <GroupIcon />
                                      )}
                                    </Avatar>
                                  </Badge>
                                </Grid>

                                <Grid item>
                                  <Link
                                    to={`/conversations/messages/${group.id}`}
                                    className={classes.iconLinkPropNormal}
                                  >
                                    <Typography
                                      className={classes.mL1Heading}
                                      fontWeight={700}
                                    >
                                      {group.subject || "Unnamed Group"}
                                    </Typography>
                                  </Link>

                                  {group.unread_count > 0 ? (
                                    <Typography
                                      variant="body2"
                                      color="green"
                                      fontWeight={600}
                                    >
                                      🔔 You have {group.unread_count} unread{" "}
                                      {group.unread_count === 1
                                        ? "message"
                                        : "messages"}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No new messages – group is up to date
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>

                              {/* Created info */}
                              <Grid
                                item
                                xs={12}
                                md={4}
                                sx={{ textAlign: { xs: "left", md: "right" } }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  {" "}
                                  {dayjs(group.created_at).format(
                                    "MMM D, YYYY"
                                  )}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Created by User #
                                  {userMap.get(group.created_by)}
                                </Typography>
                              </Grid>

                              {!isMobile && (
                                <Grid item xs={8} sx={{ mt: 3 }}>
                                  <Typography
                                    color="text.secondary"
                                    className={classes.mL1Heading2}
                                    fontWeight={600}
                                    mb={1}
                                  >
                                    Group Members
                                  </Typography>
                                  {group.staffIds?.length ? (
                                    <Stack
                                      direction="row"
                                      flexWrap="nowrap"
                                      gap={1}
                                      sx={{
                                        overflowX: "auto",
                                        maxWidth: "100%",
                                      }}
                                    >
                                      {group.staffIds.map((id) => {
                                        const name =
                                          staffMap.get(id) ?? `Staff #${id}`;
                                        return (
                                          <Link
                                            to={`/team-history/${id}`}
                                            className={
                                              classes.iconLinkPropNormal
                                            }
                                          >
                                            <Chip
                                              key={id}
                                              label={name}
                                              avatar={
                                                <Avatar
                                                  sx={{
                                                    bgcolor: "#1976d2",
                                                    color: "#fff !important",
                                                    fontWeight: 600,
                                                    fontSize: "14px",
                                                  }}
                                                >
                                                  {name.charAt(0)}
                                                </Avatar>
                                              }
                                              // onClick={() =>
                                              //   window.open(
                                              //     `${BASENAME}/team-history/${id}`,
                                              //     "_blank"
                                              //   )
                                              // }
                                              sx={{
                                                bgcolor: "#e3f2fd",
                                                color: "#1565c0",
                                                cursor: "pointer",
                                                "&:hover": {
                                                  bgcolor: "#bbdefb",
                                                },
                                                "& .MuiTouchRipple-root": {
                                                  display: "none",
                                                },
                                              }}
                                            />
                                          </Link>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No members in this group.
                                    </Typography>
                                  )}
                                </Grid>
                              )}
                              <Grid
                                item
                                sm={12}
                                xs={12}
                                md={4}
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <Button
                                  className={classes.mL12ButtonDelate}
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleOpenDeletePopup(group)}
                                >
                                  Remove Group
                                </Button>
                              </Grid>
                            </Grid>
                          </Card>
                        </Grid>
                      ))}

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Grid
                        spacing={5}
                        p={"18px"}
                        py={4}
                        justifyContent={"end"}
                        container
                        textAlign={"center"}
                        alignContent={"center"}
                        alignItems={"center"}
                      >
                        <Grid
                          sx={{
                            textAlign: "end",
                            justifyContent: "flex-end",
                            alignItems: "flex-end",
                          }}
                        >
                          <Pagination
                            count={coursings.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handleChangePage}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {coursingToDelete && (
                <DeletePopup
                  open={openDeleteCategory}
                  handleCloseDelete={handleCloseDeletePopup}
                  onConfirm={async () => {
                    await handleDelete(coursingToDelete.id.toString());
                  }}
                  title="Delete Group Confirmation"
                  content={`Are you sure you want to delete the group "${coursingToDelete.subject}"? This action cannot be undone.`}
                />
              )}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
